import React from 'react';
import { Video, Shield, Clock, Smartphone, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

const TeleconsultationSection = () => {
  const features = [
    { icon: Video, text: "Consultation vidéo HD sécurisée" },
    { icon: Clock, text: "Disponible 7j/7, sans déplacement" },
    { icon: Shield, text: "Données protégées et chiffrées" },
    { icon: Smartphone, text: "Accessible sur tous vos appareils" },
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Video className="w-4 h-4" />
              Nouveau sur vetoRDV
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              La téléconsultation <br />
              <span className="text-blue-200">vétérinaire</span>
            </h2>
            
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Consultez un vétérinaire depuis chez vous, en toute simplicité. 
              Idéal pour les conseils, suivis post-opératoires et premiers avis médicaux.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-blue-50">{feature.text}</span>
                  </div>
                );
              })}
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/recherche">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                  data-testid="teleconsult-cta-btn"
                >
                  Trouver un vétérinaire
                </Button>
              </Link>
              <Link to="/espace-veterinaire">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-6 text-lg rounded-xl"
                >
                  Espace vétérinaire
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Right - Visual */}
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
              {/* Mockup of video call */}
              <div className="bg-gray-900 rounded-2xl aspect-video relative overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900">
                  {/* Main video area */}
                  <div className="absolute inset-4 bg-gradient-to-br from-blue-900/50 to-indigo-900/50 rounded-xl flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <Video className="w-12 h-12 text-white" />
                      </div>
                      <p className="text-white/80 text-sm">Consultation en cours...</p>
                    </div>
                  </div>
                  
                  {/* Small self-view */}
                  <div className="absolute bottom-6 right-6 w-32 h-24 bg-gradient-to-br from-green-800 to-green-900 rounded-lg border-2 border-white/30 flex items-center justify-center">
                    <span className="text-white/60 text-xs">Vous</span>
                  </div>
                  
                  {/* Controls bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-black/50 backdrop-blur-sm flex items-center justify-center gap-4 px-4">
                    <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                      <Video className="w-5 h-5 text-white" />
                    </div>
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">✕</span>
                    </div>
                    <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                      <span className="text-white">🎤</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">24/7</p>
                  <p className="text-blue-200 text-sm">Disponibilité</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">HD</p>
                  <p className="text-blue-200 text-sm">Qualité vidéo</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">100%</p>
                  <p className="text-blue-200 text-sm">Sécurisé</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeleconsultationSection;
